import { Box, CircularProgress, Flex } from '@ouster/webviz-react-components';

import type { ReactElement } from 'react';

import DashboardCard from './DashboardCard';
import { GdprDataCheckPayload } from '../../reducers/adminDashboard';

type Props = {
  isLoading: boolean;
  gdprInfo: GdprDataCheckPayload | null;
};

const formatFieldLabel = (key: string) =>
  key
    .split('_')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');

const renderMapSection = (
  title: string,
  data?: Record<string, unknown>,
  sortByNumericValueDesc = false
): ReactElement | null => {
  if (!data) {
    return null;
  }

  const entries = Object.entries(data);
  if (entries.length === 0) {
    return null;
  }

  const displayedEntries = sortByNumericValueDesc
    ? [...entries].sort((a, b) => {
        const aValue =
          typeof a[1] === 'number' ? a[1] : Number.NEGATIVE_INFINITY;
        const bValue =
          typeof b[1] === 'number' ? b[1] : Number.NEGATIVE_INFINITY;
        if (bValue !== aValue) {
          return bValue - aValue;
        }
        return a[0].localeCompare(b[0]);
      })
    : entries;

  return (
    <DashboardCard title={title}>
      <Box display="grid" rowGap={3}>
        {displayedEntries.map(([key, value]) => (
          <Box
            key={`${title}-${key}`}
            display="flex"
            justifyContent="space-between"
            borderBottom="1px solid"
            borderColor="whiteAlpha.200"
            pb={3}
          >
            <Box color="gray.300">{formatFieldLabel(key)}</Box>
            <Box fontWeight="bold">{String(value)}</Box>
          </Box>
        ))}
      </Box>
    </DashboardCard>
  );
};

export default function UserGdprCheckResult({ isLoading, gdprInfo }: Props) {
  const warningsValue = gdprInfo?.warnings;
  const warnings = Array.isArray(warningsValue) ? warningsValue : [];
  const warningMessage = warnings.length > 0 ? warnings.join(' ') : null;
  const isEmptyResult =
    !isLoading && gdprInfo !== null && Object.keys(gdprInfo).length === 0;

  if (isLoading)
    return (
      <Flex
        fontSize="xl"
        color="gray.300"
        alignItems="center"
        gap={6}
        flexDirection="column"
        justifyContent="center"
        height="100%"
      >
        Checking user GDPR data...
        <CircularProgress isIndeterminate size={16} />
      </Flex>
    );

  if (isEmptyResult)
    return (
      <Flex
        fontSize="2xl"
        color="gray.300"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        No user found
      </Flex>
    );

  return (
    <DashboardCard
      title="GDPR Check Result"
      data-testid="gdpr-check-detail-card"
      style={{
        height: '100%',
        overflow: 'auto',
        borderRadius: '0px',
        alignContent: 'start',
      }}
    >
      <Box rowGap="1rem" display="grid" height="100%" overflow="auto">
        <DashboardCard title="Summary">
          <Box display="grid" rowGap={4}>
            <Box>
              <b>Total Records Affected:</b>{' '}
              {String(gdprInfo?.total_records_affected ?? 0)}
            </Box>
            <Box lineHeight={1.5}>
              <b>Warning:</b>{' '}
              {warningMessage ? (
                <Box as="span" color="yellow.300">
                  {warningMessage}
                </Box>
              ) : (
                'No warnings'
              )}
            </Box>
          </Box>
        </DashboardCard>

        {renderMapSection('User Info', gdprInfo?.user_info)}

        {renderMapSection('Data Counts', gdprInfo?.data_counts, true)}
      </Box>
    </DashboardCard>
  );
}
